import DOMPurify, { type Config } from 'dompurify';

const toHtmlString = (value: unknown) => String(value ?? '');

export const sanitizeHtml = (value: unknown, config?: Config): string => {
	try {
		return DOMPurify.sanitize(toHtmlString(value), config) as string;
	} catch {
		return '';
	}
};

// Paint/presentation CSS properties that have an equivalent SVG attribute, so
// they can be moved off a <style> rule and onto the shape.
const SVG_PAINT_PROPS = new Set([
	'fill',
	'fill-rule',
	'fill-opacity',
	'stroke',
	'stroke-width',
	'stroke-linecap',
	'stroke-linejoin',
	'stroke-miterlimit',
	'stroke-dasharray',
	'stroke-dashoffset',
	'stroke-opacity',
	'opacity',
	'color',
	'stop-color',
	'stop-opacity',
	'clip-rule',
]);

const isSafePaintValue = (value: string): boolean => {
	if (!value || /[<>"]/.test(value)) return false;
	const lower = value.toLowerCase();
	if (/javascript:|expression\(|@import|&#|\/\*/.test(lower)) return false;
	if (lower.includes('url(')) {
		const total = (lower.match(/url\(/g) ?? []).length;
		const internal = (lower.match(/url\(\s*['"]?#/g) ?? []).length;
		if (total !== internal) return false;
	}
	return true;
};

const simpleClassSelector = (selector: string): string | null => {
	const sel = selector.trim();
	if (!sel.startsWith('.')) return null;
	const name = sel.slice(1);
	if (!name || /[ \t\r\n>+~.#:[\](),*]/.test(name)) return null;
	return name;
};

// Many logos keep their colours in a <style> block (CSS classes) rather than on
// the shapes. The sanitizer below drops <style> (CSS is an injection surface),
// which would flatten such a logo, so inline the class paint rules onto the
// shapes as presentation attributes first — matching what the backend persists,
// so the upload preview looks like the saved result. DOMPurify still runs after.
const inlineSvgStyleColors = (svg: string): string => {
	if (typeof DOMParser === 'undefined' || typeof document === 'undefined') {
		return svg;
	}
	if (!/<style[\s>]/i.test(svg)) {
		return svg;
	}

	let doc: Document;
	try {
		doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
	} catch {
		return svg;
	}
	if (doc.getElementsByTagName('parsererror').length > 0) {
		return svg;
	}

	const styleEls = Array.from(doc.getElementsByTagName('style'));
	if (styleEls.length === 0) {
		return svg;
	}

	const classStyles = new Map<string, Map<string, string>>();
	const ruleRe = /([^{}]+)\{([^{}]*)\}/g;
	for (const styleEl of styleEls) {
		const css = (styleEl.textContent ?? '')
			.replace(/<!\[CDATA\[/g, '')
			.replace(/\]\]>/g, '');
		let match: RegExpExecArray | null;
		while ((match = ruleRe.exec(css)) !== null) {
			const body = match[2];
			for (const selector of match[1].split(',')) {
				const className = simpleClassSelector(selector);
				if (!className) {
					continue;
				}
				let decls = classStyles.get(className);
				if (!decls) {
					decls = new Map();
					classStyles.set(className, decls);
				}
				for (const declaration of body.split(';')) {
					const idx = declaration.indexOf(':');
					if (idx < 0) {
						continue;
					}
					const prop = declaration.slice(0, idx).trim().toLowerCase();
					const value = declaration
						.slice(idx + 1)
						.replace(/!important/i, '')
						.trim();
					if (SVG_PAINT_PROPS.has(prop) && isSafePaintValue(value)) {
						decls.set(prop, value);
					}
				}
			}
		}
	}
	if (classStyles.size === 0) {
		return svg;
	}

	doc.querySelectorAll('[class]').forEach((el) => {
		const merged = new Map<string, string>();
		for (const className of (el.getAttribute('class') ?? '').split(/\s+/)) {
			const decls = classStyles.get(className);
			if (decls) {
				for (const [prop, value] of decls) {
					merged.set(prop, value);
				}
			}
		}
		for (const [prop, value] of merged) {
			if (!el.hasAttribute(prop)) {
				el.setAttribute(prop, value);
			}
		}
	});

	styleEls.forEach((el) => el.parentNode?.removeChild(el));
	return new XMLSerializer().serializeToString(doc);
};

export const sanitizeSvg = (value: unknown): string =>
	sanitizeHtml(inlineSvgStyleColors(toHtmlString(value)), {
		USE_PROFILES: { svg: true, svgFilters: true },
		FORBID_TAGS: ['script', 'foreignObject'],
	});
